/**
 * React NodeView for `videoEmbed` — Phase 4c block-level atom.
 *
 * Resolves a presigned GET URL via `useFilePresignedUrl(fileId)` and
 * hands it to the shared `<AttachmentVideoPlayer>`, which owns the
 * `<video>` element, the bounded network re-presign, and the
 * "unplayable here" card for a codec this browser can't decode. The
 * card surfaces render the same primitive with their own chrome, so the
 * MediaError machine exists once.
 *
 * `unplayable` is MIRRORED here (as the file id the browser rejected)
 * purely so the wrapper can stamp `data-state="unplayable"` and so the
 * loading branch stays ordered behind it — a refresh still in flight
 * must not flash a skeleton over a decision already made. Deriving it
 * from `fileId` rather than storing a boolean is what makes it reset
 * for free when the embed points at a new file.
 *
 * Caption: static text under the video. Click-to-edit inline via an
 * `<input>` that swaps in on click, commits via blur / Enter, cancels
 * via Esc. Persisted via `updateAttributes`.
 *
 * Visual contract:
 *   - Outer block wrapper carries `data-block-type="video-embed"` for
 *     Playwright selectors + the parseHTML round-trip.
 *   - Themed border at `border-border`, themed background at
 *     `bg-bg-elevated`, padded by 0.5rem.
 *   - Video sits inside a max-width 720px container centered in the
 *     editor's 880px column.
 *   - Caption row uses `text-fg-muted text-xs italic` mirroring the
 *     Confluence convention.
 *
 * Loading / restricted / deleted fallbacks share the same chrome as
 * the work-item embed for visual consistency. Empty `fileId` is the
 * "placeholder" state shown before the upload modal commits.
 */

import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import { AlertCircle, Loader2, RotateCcw, Upload, Video as VideoIcon, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentVideoPlayer } from '@/components/AttachmentVideoPlayer'
import { UploadProgressBar, uploadPercent } from '@/features/uploads/UploadProgressBar'
import { useFilePresignedUrl } from '@/features/wiki/hooks/use-file-presigned-url'
import { useUploadProgress } from '@/features/wiki/hooks/use-upload-guards'
import {
  CANCEL_EMBED_UPLOAD_EVENT,
  type CancelEmbedUploadDetail,
  DISMISS_EMBED_UPLOAD_EVENT,
  type EmbedUploadActionDetail,
  OPEN_VIDEO_UPLOAD_EVENT,
  type OpenEmbedDetail,
  RETRY_EMBED_UPLOAD_EVENT,
} from '@/features/wiki/lib/embed-events'
import { formatPercent } from '@/i18n/formatters'

export function VideoEmbedView({
  node,
  updateAttributes,
  getPos,
  editor,
  deleteNode,
}: ReactNodeViewProps<HTMLElement>) {
  const { t, i18n } = useTranslation('wiki')
  const fileId = (node.attrs.fileId as string | null) ?? null
  const caption = (node.attrs.caption as string) ?? ''
  const uploadingPlaceholderId = (node.attrs.uploadingPlaceholderId as string | null) ?? null
  const uploadProgress = useUploadProgress(uploadingPlaceholderId)
  const uploadPercentValue = uploadPercent(uploadProgress)
  const uploadingFailed = (node.attrs.uploadingFailed as boolean) ?? false
  const uploadingFileName = (node.attrs.uploadingFileName as string | null) ?? null

  const { url, isLoading, status, refresh } = useFilePresignedUrl(fileId)
  // The file id whose bytes the browser has rejected, NOT a boolean: the
  // comparison below resets the state for free when the upload flow
  // writes a new `fileId` onto this same node instance, without a
  // during-render setState and without duplicating the primitive's own
  // reset.
  const [unplayableFileId, setUnplayableFileId] = useState<string | null>(null)
  const unplayable = fileId !== null && unplayableFileId === fileId

  // Caption editing — local state during edit, persisted on commit.
  const [editingCaption, setEditingCaption] = useState(false)
  const [captionDraft, setCaptionDraft] = useState(caption)

  // Copy for the shared player primitive. The wiki keeps its own
  // `wiki.video.*` catalog entries; the card surfaces pass their
  // `uploads.embed.video*` equivalents into the same slots.
  const playerLabels = {
    ariaLabel: t('video.ariaLabel', { defaultValue: 'Embedded video' }),
    fallback: t('video.fallback', { defaultValue: 'Your browser cannot play this video.' }),
    unplayableTitle: t('video.unplayable'),
    unplayableBody: t('video.unplayableBody'),
    download: t('video.download'),
    downloadFailed: t('video.downloadFailed'),
  }

  // FAILED — the upload errored; the panel stays visible with Retry /
  // Dismiss instead of silently disappearing. Checked before UPLOADING;
  // the orchestrator kept the original File keyed on the placeholder id
  // so Retry re-runs the network leg against this same node. Never
  // persists — the strip removes it from every flush / IDB write.
  if (uploadingPlaceholderId && uploadingFailed) {
    const dispatchAction = (eventName: string) => (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (typeof window === 'undefined') return
      window.dispatchEvent(
        new CustomEvent<EmbedUploadActionDetail>(eventName, {
          detail: { key: uploadingPlaceholderId },
        }),
      )
    }
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="video-embed"
        data-state="error"
        className="relative my-3 mx-auto w-full max-w-[720px] rounded-md border border-danger/40 bg-bg-muted p-6 text-center"
        contentEditable={false}
      >
        <div className="flex flex-col items-center gap-2 text-sm text-fg-muted">
          <AlertCircle aria-hidden="true" size={24} className="text-danger" />
          {uploadingFileName ? (
            <span className="max-w-full truncate text-fg" title={uploadingFileName}>
              {uploadingFileName}
            </span>
          ) : null}
          <span className="text-danger">{t('docDrop.failedLabel')}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={dispatchAction(RETRY_EMBED_UPLOAD_EVENT)}
              onMouseDown={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs font-medium text-fg hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <RotateCcw aria-hidden="true" size={12} />
              {t('docDrop.retry')}
            </button>
            <button
              type="button"
              onClick={dispatchAction(DISMISS_EMBED_UPLOAD_EVENT)}
              onMouseDown={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-fg-muted hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t('docDrop.dismiss')}
            </button>
          </div>
        </div>
      </NodeViewWrapper>
    )
  }

  // UPLOADING — an OS-drop upload is in flight. Checked BEFORE the
  // `!fileId` picker placeholder below: an uploading node ALSO has
  // `fileId === null`, and the picker branch's reopen / remove buttons
  // are exactly the wrong affordances mid-upload. Reuses the loading
  // panel's chrome (border-border / bg-bg-muted / max-w 720px) with the
  // filename + a corner Cancel that hands off to the orchestrator (it
  // owns the AbortController + the attachment-row cleanup). Plain
  // inline handlers — no new hooks, so the hook count stays stable
  // across state flips.
  if (uploadingPlaceholderId) {
    const cancelUpload = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (typeof window === 'undefined') return
      window.dispatchEvent(
        new CustomEvent<CancelEmbedUploadDetail>(CANCEL_EMBED_UPLOAD_EVENT, {
          detail: { uploadingPlaceholderId },
        }),
      )
    }
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="video-embed"
        data-state="uploading"
        className="relative my-3 mx-auto w-full max-w-[720px] rounded-md border border-border bg-bg-muted p-6 text-center"
        contentEditable={false}
      >
        <div className="flex flex-col items-center gap-2 text-sm text-fg-muted">
          <Loader2
            aria-hidden="true"
            size={24}
            className="animate-spin motion-reduce:animate-none"
          />
          {uploadingFileName ? (
            <span className="max-w-full truncate text-fg" title={uploadingFileName}>
              {uploadingFileName}
            </span>
          ) : null}
          <span>
            {t('video.uploading', { defaultValue: 'Uploading video…' })}
            {/* The number carries the answer; the bar only shows the shape
             * of it. Absent until the first byte event — a zero would read
             * as a stall, and the spinner above already says "started". */}
            {uploadPercentValue !== null ? (
              <span className="ms-1 font-medium text-fg tabular-nums">
                {formatPercent(uploadPercentValue / 100, i18n.language)}
              </span>
            ) : null}
          </span>
          {/* The drop pipeline publishes byte progress under the
           * placeholder id — the only handle this NodeView has on the
           * upload driving it. Without reading it the panel showed a
           * spinner and nothing else, so a 200 MB video looked identical
           * at 3% and at 97%. Null until the first byte event, which the
           * bar draws as indeterminate rather than as a zero. */}
          <span className="block w-full max-w-[420px]">
            <UploadProgressBar
              progress={uploadProgress}
              label={t('uploads.progressAria', { filename: uploadingFileName ?? '' })}
              // This panel is painted `bg-bg-muted`, which is also the
              // bar's default track — so the track vanished into it and
              // only the fill showed, reading as a stray rule.
              trackBg="bg-border"
            />
          </span>
        </div>
        <button
          type="button"
          onClick={cancelUpload}
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute end-2 top-2 inline-flex items-center justify-center rounded-md p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={t('attachment.cancel')}
          title={t('attachment.cancel')}
        >
          <X aria-hidden="true" size={14} />
        </button>
      </NodeViewWrapper>
    )
  }

  // Placeholder — file not yet picked / uploaded.
  // Clickable so the user can re-open the upload modal after dismissing
  // it accidentally; the Remove button gives a clear way out without
  // needing Backspace. The outer wrapper carries `mx-auto max-w-[720px]`
  // so the dashed border doesn't run flush against the doc card edge
  // (where it visually clipped against the surrounding chrome).
  if (!fileId) {
    const reopenUploadModal = () => {
      if (typeof window === 'undefined') return
      const pos = typeof getPos === 'function' ? getPos() : undefined
      if (typeof pos !== 'number') return
      window.dispatchEvent(
        new CustomEvent<OpenEmbedDetail>(OPEN_VIDEO_UPLOAD_EVENT, { detail: { pos } }),
      )
    }
    const removeNode = (e: React.MouseEvent) => {
      e.stopPropagation()
      // Prefer the NodeView-provided `deleteNode` so we don't have to
      // resolve the position ourselves.
      if (typeof deleteNode === 'function') {
        deleteNode()
        return
      }
      // Fallback — resolve via getPos + delete the node's range
      // explicitly so the placeholder always has a removal path even
      // when TipTap doesn't surface `deleteNode` (older `@tiptap/react`
      // typings).
      const pos = typeof getPos === 'function' ? getPos() : undefined
      if (typeof pos !== 'number' || !editor) return
      editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + node.nodeSize })
        .run()
    }
    // `onMouseDown.stopPropagation` on both buttons keeps ProseMirror's
    // selection-on-mousedown for the atomic videoEmbed node from
    // swallowing the click intent — without it, clicks land as a node
    // selection and the placeholder reads as un-dismissable except via
    // Backspace. Same fix lands on FileEmbedView's placeholder.
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="video-embed"
        data-state="placeholder"
        className="relative my-3 mx-auto w-full max-w-[720px]"
        // contentEditable=false belt-and-braces: the wrapper is already a
        // ReactNodeView, but TipTap occasionally surfaces caret events on
        // the placeholder if the inner span isn't marked. This keeps the
        // click handler's target stable.
        contentEditable={false}
      >
        <button
          type="button"
          onClick={reopenUploadModal}
          onMouseDown={(e) => e.stopPropagation()}
          className="group flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-bg-muted p-6 text-center text-sm text-fg-muted transition-colors hover:border-accent hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={t('video.placeholderAriaLabel', {
            defaultValue: 'Pick a video to embed',
          })}
        >
          <span className="inline-flex items-center gap-2 text-fg">
            <VideoIcon aria-hidden="true" size={18} />
            <Upload aria-hidden="true" size={14} />
          </span>
          <span>{t('video.placeholder', { defaultValue: 'Pick a video to embed' })}</span>
          <span className="text-xs text-fg-subtle">
            {t('video.placeholderHint', {
              defaultValue: 'Click to choose a file — MP4, WebM, or MOV',
            })}
          </span>
        </button>
        <button
          type="button"
          onClick={removeNode}
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute end-2 top-2 inline-flex items-center justify-center rounded-md p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={t('video.placeholderRemove', { defaultValue: 'Remove' })}
          title={t('video.placeholderRemove', { defaultValue: 'Remove' })}
        >
          <X aria-hidden="true" size={14} />
        </button>
      </NodeViewWrapper>
    )
  }

  if (status === 403) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="video-embed"
        data-file-id={fileId}
        data-state="restricted"
        className="my-3 rounded-md border border-border bg-bg-muted p-4 text-sm text-fg-subtle"
      >
        {t('video.restricted', { defaultValue: 'Video unavailable — restricted.' })}
      </NodeViewWrapper>
    )
  }

  if (status === 404) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="video-embed"
        data-file-id={fileId}
        data-state="deleted"
        className="my-3 rounded-md border border-border bg-bg-muted p-4 text-sm text-fg-subtle"
      >
        {t('video.deleted', { defaultValue: 'Video unavailable — deleted.' })}
      </NodeViewWrapper>
    )
  }

  // UNPLAYABLE — the browser rejected the codec / container, or a network
  // error survived the bounded re-presign. Checked before the loading
  // branch so a refresh that is still in flight can't flash a skeleton
  // over a decision we have already made. The bytes are intact, so the
  // card offers them rather than implying a failed upload.
  if (unplayable) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="video-embed"
        data-file-id={fileId}
        data-state="unplayable"
        className="relative my-3 mx-auto w-full max-w-[720px]"
        contentEditable={false}
      >
        <AttachmentVideoPlayer
          fileId={fileId}
          url={url}
          refresh={refresh}
          onUnplayable={() => setUnplayableFileId(fileId)}
          className="flex w-full flex-col items-center gap-2 rounded-md border border-border bg-bg-elevated p-6 text-center"
          labels={playerLabels}
        />
      </NodeViewWrapper>
    )
  }

  if (isLoading || !url) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="video-embed"
        data-file-id={fileId}
        data-state="loading"
        className="my-3 animate-pulse rounded-md border border-border bg-bg-muted p-8 text-center text-sm text-fg-subtle"
      >
        {t('video.loading', { defaultValue: 'Loading video…' })}
      </NodeViewWrapper>
    )
  }

  return (
    <NodeViewWrapper
      as="div"
      data-block-type="video-embed"
      data-file-id={fileId}
      data-state="ready"
      className="my-3 flex flex-col items-center gap-1 rounded-md border border-border bg-bg-elevated p-2"
    >
      <AttachmentVideoPlayer
        fileId={fileId}
        url={url}
        refresh={refresh}
        onUnplayable={() => setUnplayableFileId(fileId)}
        className="flex w-full flex-col items-center gap-2 rounded-md border border-border bg-bg-elevated p-6 text-center"
        videoClassName="max-h-[480px] w-full max-w-[720px] rounded bg-black"
        labels={playerLabels}
      />
      {editingCaption ? (
        <input
          type="text"
          value={captionDraft}
          onChange={(e) => setCaptionDraft(e.target.value)}
          onBlur={() => {
            updateAttributes({ caption: captionDraft })
            setEditingCaption(false)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              updateAttributes({ caption: captionDraft })
              setEditingCaption(false)
            } else if (e.key === 'Escape') {
              e.preventDefault()
              setCaptionDraft(caption)
              setEditingCaption(false)
            }
          }}
          placeholder={t('video.captionPlaceholder', { defaultValue: 'Add caption' })}
          aria-label={t('video.captionAriaLabel', { defaultValue: 'Video caption' })}
          // biome-ignore lint/a11y/noAutofocus: edit-mode flip is a user gesture — same pattern as the existing EditableField surfaces.
          autoFocus
          className="w-full max-w-[720px] rounded border border-border bg-bg-elevated px-2 py-1 text-center text-xs italic text-fg focus:outline-none focus:ring-1 focus:ring-accent"
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setCaptionDraft(caption)
            setEditingCaption(true)
          }}
          className="w-full max-w-[720px] truncate rounded px-2 py-1 text-center text-xs italic text-fg-muted hover:bg-bg-muted"
          title={t('video.captionEditTitle', { defaultValue: 'Edit caption' })}
        >
          {caption || t('video.captionPlaceholder', { defaultValue: 'Add caption' })}
        </button>
      )}
    </NodeViewWrapper>
  )
}

VideoEmbedView.displayName = 'VideoEmbedView'
